/**
 * CR-7750: the dialog for a claim that has no register row behind it.
 *
 * A supplier's FIRST submission for an accreditation creates no
 * SupplierAccreditation, so the matrix has no cell to open — only a marker on
 * an otherwise-empty one. This is what that marker opens, for both roles:
 * the customer reviews it, the supplier sees it is with them.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { ApprovalRegisterPending } from '../../lib/api';
import PendingReviewPanel from './PendingReviewPanel';
import SupplierPendingNotice from './SupplierPendingNotice';

export interface PendingOnlyDialogProps {
  pending: ApprovalRegisterPending | null;
  isCustomer: boolean;
  formatDate: (value: string) => string;
  onClose: () => void;
  /** Hands back the whole submission — the dialog needs its id and values to
   *  amend it, not just which accreditation it was against. */
  onAmend: (pending: ApprovalRegisterPending) => void;
  onChanged: () => void;
}

export default function PendingOnlyDialog({
  pending,
  isCustomer,
  formatDate,
  onClose,
  onAmend,
  onChanged,
}: PendingOnlyDialogProps) {
  const fmt = (value: string | null | undefined) => (value ? formatDate(value) : '—');

  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {pending && (
          <>
            <DialogHeader>
              <DialogTitle>Awaiting review</DialogTitle>
              <DialogDescription>
                This approval is not on the register yet.
              </DialogDescription>
            </DialogHeader>
            {isCustomer ? (
              <PendingReviewPanel
                pending={pending}
                current={null}
                formatDate={fmt}
                onReviewed={onChanged}
              />
            ) : (
              <SupplierPendingNotice
                pending={pending}
                formatDate={fmt}
                onAmend={onAmend}
                onChanged={onChanged}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
